import { FC, useRef, useState } from 'react';
import Button from '@/components/UI/Button';
import { errorToast, successToast } from '@/helpers';
import { _notNil } from '@/littledash';
import { useMountedState } from '@/support/Hooks/fetch/useMountedState';

export interface UploadConfig {
  url: string;
  fields: Record<string, string>;
}

interface SamlMetadataFileUploadProps {
  uploadConfig: UploadConfig | null;
  downloadUrl: string | null;
  onUploadComplete?: () => void | Promise<void>;
}

const MAX_FILE_SIZE = 0.5 * 1024 * 1024;

const SamlMetadataFileUpload: FC<SamlMetadataFileUploadProps> = ({ uploadConfig, downloadUrl, onUploadComplete }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isMounted = useMountedState();

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const isXmlExtension = file.name.toLowerCase().endsWith('.xml');
    const isXmlMimeType = file.type === 'application/xml' || file.type === 'text/xml';

    if (!isXmlExtension || !isXmlMimeType) {
      errorToast('Please upload a valid XML file');
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      errorToast('File size must be 0.5MB or less');
      return;
    }

    if (!uploadConfig) {
      errorToast('Unable to upload file. Please contact support if this issue persists.');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();

      Object.entries(uploadConfig.fields).forEach(([key, value]) => {
        formData.append(key, value);
      });

      // File must be appended LAST (S3 POST requirement)
      formData.append('file', file);

      const uploadResponse = await fetch(uploadConfig.url, {
        method: 'POST',
        body: formData,
      });

      if (!uploadResponse.ok) {
        throw new Error('File upload failed');
      }

      successToast(`Uploaded ${file.name}`);

      if (onUploadComplete) {
        await onUploadComplete();
      }
    } catch {
      if (isMounted()) {
        errorToast('Failed to upload metadata file');
      }
    } finally {
      if (isMounted()) {
        setUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    }
  };

  const handleDownload = () => {
    if (!downloadUrl) {
      return;
    }
    window.open(downloadUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div data-testid="saml-metadata-file-upload">
      <label htmlFor="saml-metadata-file-input" className="db mb3">
        Metadata XML File (0.5MB max)
      </label>

      <input
        id="saml-metadata-file-input"
        ref={fileInputRef}
        type="file"
        accept=".xml"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        data-testid="file-input"
      />

      <div className="flex items-center">
        <Button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="mr3"
          testId="upload-button"
        >
          {uploading ? 'Uploading...' : _notNil(downloadUrl) ? 'Replace File' : 'Upload File'}
        </Button>

        {_notNil(downloadUrl) && (
          <div data-testid="current-file-info">
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleDownload();
              }}
              className="link blue f6"
              data-testid="download-link"
            >
              Download
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default SamlMetadataFileUpload;
